import { add, init, reset, setGroup, setUserId, track } from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { LangParam } from '../types';
import { getAmplitudeApiKeyWithFallback, ServiceType } from './amplitudeConfig';

/**
 * Set Amplitude user identity and account group.
 * @param userId The user's ID
 * @param accountId The account/company ID for group analytics
 */
export function setAmplitudeIdentity(userId: number, accountId: number) {
  if (process.env.REACT_APP_ENABLE_TRACKING !== 'true') return;
  try {
    setUserId(String(userId));
    setGroup('account', String(accountId));
  } catch (e) {
    console.log('Failed to set Amplitude identity', e);
  }
}

/**
 * Clear Amplitude user identity on logout.
 */
export function clearAmplitudeIdentity() {
  if (process.env.REACT_APP_ENABLE_TRACKING !== 'true') return;
  try {
    reset();
  } catch (e) {
    console.log('Failed to clear Amplitude identity', e);
  }
}

/**
 * A centralized event tracking system.
 * It catch any exception internally.
 * @param event Event name.
 * @param metadata Event metadata, can be undefined or an object with custom attributes.
 * @param strategy Trackers to be activated.
 */
export function datascopeTracker(
  event: string,
  metadata: unknown,
  strategy: 'ALL' | 'AMP' = 'ALL',
) {
  // Exit early if tracking is disabled
  if (process.env.REACT_APP_ENABLE_TRACKING !== 'true') {
    return;
  }

  try {
    switch (strategy) {
      case 'ALL':
        // Google Analytics
        // Facebook Pixel
        // Amplitude
        amplitudStrategy(event, metadata);
        break;
      case 'AMP':
        amplitudStrategy(event, metadata);
        break;
    }
  } catch (e) {
    console.error(e);
  }
}
/**
 * Amplitud event tracker
 * @param event Event name.
 * @param metadata Metadata object. Can be undefined
 */
function amplitudStrategy(event: string, metadata: unknown) {
  track(event, metadata);
}
/**
 * Initialize Amplitude / Google Analytics / Facebook Pixel / etc
 */
export function initializeTrackers() {
  if (process.env.REACT_APP_ENABLE_TRACKING !== 'true') return;
  try {
    const apiKey = getAmplitudeApiKeyWithFallback(ServiceType.Analytics);
    init(apiKey, {
      autocapture: {
        elementInteractions: true,
        formInteractions: true,
        fileDownloads: true,
        pageViews: true,
        sessions: true,
        attribution: true,
      },

      fetchRemoteConfig: true,
    });
    add(sessionReplayPlugin());
    add(engagementPlugin());
  } catch (e) {
    console.error('Failed to load amplitude analytics', e);
  }
}

export function initializeBeamer(userEmail: string, lang: LangParam) {
  if (window.Beamer) {
    window.Beamer.update({
      language: lang?.toUpperCase(),
      user_email: userEmail,
    });
  }
}
